import { fabric } from 'fabric';
import { DEFAULT_FONT_FAMILY } from 'shared/fonts';
import type { EditorEventMap, EditorEventName, EditorPlugin, FabricPluginName, ObjectTypeDefinition } from 'modules/fabric-editor';
import { EventBus, type Listener, type Unsubscribe } from './event-bus';
import { ObjectRegistry } from './object-registry';
import { PluginRegistry } from './plugin-registry';

export interface Resolution {
  width: number;
  height: number;
}

export interface FabricEditorOptions {
  canvasEl: HTMLCanvasElement;
  resolution?: Resolution;
  fabricOptions?: fabric.ICanvasOptions;
  autoFit?: boolean;
}

export interface AddTextOptions {
  text: string;
  left?: number;
  top?: number;
  fontSize?: number;
  fill?: string;
  /** Без шрифта Fabric ставит свой Times New Roman — шрифт вне набора редакторов. */
  fontFamily?: string;
}

export interface AddImageOptions {
  url: string;
  left?: number;
  top?: number;
  scale?: number;
}

export interface AddRectOptions {
  left?: number;
  top?: number;
  width: number;
  height: number;
  fill?: string;
}

export interface AddCircleOptions {
  left?: number;
  top?: number;
  radius: number;
  fill?: string;
}

export interface AddEllipseOptions {
  left?: number;
  top?: number;
  rx: number;
  ry: number;
  fill?: string;
}

export interface AddTriangleOptions {
  left?: number;
  top?: number;
  width: number;
  height: number;
  fill?: string;
}

export interface AddPolygonOptions {
  left?: number;
  top?: number;
  points: Array<{ x: number; y: number }>;
  fill?: string;
}

export class FabricEditor {
  readonly canvas: fabric.Canvas;
  readonly bus = new EventBus<EditorEventMap>();
  readonly objectTypes = new ObjectRegistry();
  readonly objects: ObjectsFacade;

  resolution: Resolution;
  autoFit: boolean;

  private readonly plugins = new PluginRegistry();
  private readonly fabricUnbind: Array<() => void> = [];
  private disposed = false;

  constructor(options: FabricEditorOptions) {
    this.resolution = options.resolution ?? { width: 1920, height: 1080 };
    this.autoFit = options.autoFit ?? false;
    this.canvas = new fabric.Canvas(options.canvasEl, {
      preserveObjectStacking: true,
      ...options.fabricOptions,
    });
    this.objects = new ObjectsFacade(this);
    this.bindFabricEvents();
    if (this.autoFit) this.fit();
  }

  use(plugin: EditorPlugin): this {
    this.assertNotDisposed();
    this.plugins.install(this, plugin);
    return this;
  }

  hasPlugin(name: FabricPluginName): boolean {
    return this.plugins.has(name);
  }

  registerObjectType<TProps>(def: ObjectTypeDefinition<TProps>): this {
    this.objectTypes.register(def);
    return this;
  }

  on<K extends EditorEventName>(event: K, listener: Listener<EditorEventMap[K]>): Unsubscribe {
    return this.bus.on(event, listener);
  }

  off<K extends EditorEventName>(event: K, listener: Listener<EditorEventMap[K]>): void {
    this.bus.off(event, listener);
  }

  emit<K extends EditorEventName>(event: K, payload: EditorEventMap[K]): void {
    this.bus.emit(event, payload);
  }

  setResolution(resolution: Resolution): void {
    this.resolution = resolution;
    if (this.autoFit) this.fit();
  }

  setSize(width: number, height: number): void {
    this.canvas.setDimensions({ width, height });
    this.bus.emit('canvas:resized', { width, height });
    if (this.autoFit) this.fit();
  }

  getViewportTopLeft(padding = 20): { left: number; top: number } {
    const vpt = this.canvas.viewportTransform;
    const zoom = this.canvas.getZoom() || 1;
    const tx = vpt?.[4] ?? 0;
    const ty = vpt?.[5] ?? 0;
    return {
      left: (-tx + padding) / zoom,
      top: (-ty + padding) / zoom,
    };
  }

  fit(): void {
    const cw = this.canvas.getWidth();
    const ch = this.canvas.getHeight();
    const { width: rw, height: rh } = this.resolution;
    if (cw <= 0 || ch <= 0 || rw <= 0 || rh <= 0) return;
    const scale = Math.min(cw / rw, ch / rh);
    const tx = (cw - rw * scale) / 2;
    const ty = (ch - rh * scale) / 2;
    this.canvas.setViewportTransform([scale, 0, 0, scale, tx, ty]);
    this.canvas.requestRenderAll();
    this.bus.emit('zoom:changed', { zoom: scale });
  }

  isDisposed(): boolean {
    return this.disposed;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.plugins.disposeAll(this);
    for (const unbind of this.fabricUnbind) unbind();
    this.fabricUnbind.length = 0;
    this.bus.clear();
    try {
      this.canvas.dispose();
    } catch {
      // canvas may already be torn down by React strict-mode unmount
    }
  }

  private bindFabricEvents(): void {
    const onSelection = () => {
      this.bus.emit('selection:changed', { active: this.canvas.getActiveObjects() });
    };
    const onSelectionCreated = (e: fabric.IEvent & { selected?: fabric.Object[] }) => {
      this.bus.emit('selection:created', { selected: e.selected ?? [] });
      this.bus.emit('selection:changed', { active: this.canvas.getActiveObjects() });
    };
    const onAdded = (e: fabric.IEvent) => {
      if (e.target) this.bus.emit('object:added', { target: e.target });
    };
    const onModified = (e: fabric.IEvent) => {
      if (e.target) this.bus.emit('object:modified', { target: e.target });
    };
    const onRemoved = (e: fabric.IEvent) => {
      if (e.target) this.bus.emit('object:removed', { target: e.target });
    };

    this.canvas.on('selection:created', onSelectionCreated);
    this.canvas.on('selection:updated', onSelection);
    this.canvas.on('selection:cleared', onSelection);
    this.canvas.on('object:added', onAdded);
    this.canvas.on('object:modified', onModified);
    this.canvas.on('object:removed', onRemoved);

    this.fabricUnbind.push(
      () => this.canvas.off('selection:created', onSelectionCreated as never),
      () => this.canvas.off('selection:updated', onSelection as never),
      () => this.canvas.off('selection:cleared', onSelection as never),
      () => this.canvas.off('object:added', onAdded as never),
      () => this.canvas.off('object:modified', onModified as never),
      () => this.canvas.off('object:removed', onRemoved as never),
    );
  }

  private assertNotDisposed(): void {
    if (this.disposed) throw new Error('FabricEditor has been disposed.');
  }
}

// Offset applied to a duplicated object so the clone is visibly nudged off the
// original. `data` is preserved on clone so business identity (placeholder
// kind, etc.) survives the copy — it is not in Fabric's default toObject set.
const DUPLICATE_OFFSET = 10;
const DUPLICATE_PROPS = ['data'];

export class ObjectsFacade {
  constructor(private readonly editor: FabricEditor) {}

  addText(options: AddTextOptions): fabric.Textbox {
    const fallback = this.editor.getViewportTopLeft();
    const obj = new fabric.Textbox(options.text, {
      left: options.left ?? fallback.left,
      top: options.top ?? fallback.top,
      fontSize: options.fontSize ?? 32,
      fontFamily: options.fontFamily ?? DEFAULT_FONT_FAMILY,
      fill: options.fill ?? '#000000',
      // Fabric defaults strokeWidth to 1 even with stroke=null; that phantom
      // pixel inflates the bounding box (selection gap) and skews snap-to-grid
      // (the fill edge lands strokeWidth off the grid). Default to 0; the
      // inspector re-introduces a stroke explicitly when wanted.
      strokeWidth: 0,
    });
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  addRect(options: AddRectOptions): fabric.Rect {
    const fallback = this.editor.getViewportTopLeft();
    const obj = new fabric.Rect({
      left: options.left ?? fallback.left,
      top: options.top ?? fallback.top,
      width: options.width,
      height: options.height,
      fill: options.fill ?? '#cccccc',
      // See addText: zero the phantom default stroke so the bounding box hugs
      // the fill and snap-to-grid aligns the visible edge.
      strokeWidth: 0,
    });
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  addCircle(options: AddCircleOptions): fabric.Circle {
    const fallback = this.editor.getViewportTopLeft();
    const obj = new fabric.Circle({
      left: options.left ?? fallback.left,
      top: options.top ?? fallback.top,
      radius: options.radius,
      fill: options.fill ?? '#cccccc',
      strokeWidth: 0,
    });
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  addEllipse(options: AddEllipseOptions): fabric.Ellipse {
    const fallback = this.editor.getViewportTopLeft();
    const obj = new fabric.Ellipse({
      left: options.left ?? fallback.left,
      top: options.top ?? fallback.top,
      rx: options.rx,
      ry: options.ry,
      fill: options.fill ?? '#cccccc',
      strokeWidth: 0,
    });
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  addTriangle(options: AddTriangleOptions): fabric.Triangle {
    const fallback = this.editor.getViewportTopLeft();
    const obj = new fabric.Triangle({
      left: options.left ?? fallback.left,
      top: options.top ?? fallback.top,
      width: options.width,
      height: options.height,
      fill: options.fill ?? '#cccccc',
      strokeWidth: 0,
    });
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  addPolygon(options: AddPolygonOptions): fabric.Polygon {
    const fallback = this.editor.getViewportTopLeft();
    const obj = new fabric.Polygon(options.points, {
      left: options.left ?? fallback.left,
      top: options.top ?? fallback.top,
      fill: options.fill ?? '#cccccc',
      strokeWidth: 0,
    });
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  addImage(options: AddImageOptions): Promise<fabric.Image> {
    const fallback = this.editor.getViewportTopLeft();
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(
        options.url,
        (img) => {
          if (!img) {
            reject(new Error(`Failed to load image: ${options.url}`));
            return;
          }
          img.set({
            left: options.left ?? fallback.left,
            top: options.top ?? fallback.top,
            // See addText: zero the phantom default stroke so the bounding box
            // hugs the image and snap-to-grid aligns the visible edge.
            strokeWidth: 0,
          });
          if (options.scale !== undefined) img.scale(options.scale);
          this.editor.canvas.add(img);
          this.editor.canvas.setActiveObject(img);
          this.editor.canvas.requestRenderAll();
          resolve(img);
        },
        { crossOrigin: 'anonymous' },
      );
    });
  }

  addCustom<TProps>(kind: string, props: TProps): fabric.Object {
    const obj = this.editor.objectTypes.create(kind, props);
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  /**
   * Clones `source` (defaults to the active object) offset by DUPLICATE_OFFSET,
   * adds it to the canvas and selects it — preserving the `data` extension key.
   * An `ActiveSelection` is expanded into its members, matching Fabric's
   * paste-a-multiselection pattern. Fabric's `clone` is callback-based (sync for
   * plain objects, async for images), so placement runs in the callback.
   * Shared by the Ctrl+V hotkey and the toolbar Duplicate button.
   */
  duplicate(source?: fabric.Object): void {
    const target = source ?? this.editor.canvas.getActiveObject();
    if (!target) return;
    target.clone((cloned: fabric.Object) => this.placeClone(cloned), DUPLICATE_PROPS);
  }

  private placeClone(cloned: fabric.Object): void {
    const canvas = this.editor.canvas;
    canvas.discardActiveObject();
    cloned.set({
      left: (cloned.left ?? 0) + DUPLICATE_OFFSET,
      top: (cloned.top ?? 0) + DUPLICATE_OFFSET,
      evented: true,
    });
    const asSelection = cloned as fabric.ActiveSelection;
    if (typeof asSelection.forEachObject === 'function' && asSelection.type === 'activeSelection') {
      asSelection.canvas = canvas;
      asSelection.forEachObject((obj) => canvas.add(obj));
      asSelection.setCoords();
    } else {
      canvas.add(cloned);
    }
    canvas.setActiveObject(cloned);
    canvas.requestRenderAll();
  }

  /**
   * Groups the current `ActiveSelection` into a single `fabric.Group`. No-op
   * (returns null) unless the active object is an active selection. Wrapped in
   * a history batch so group + ungroup are each a single undo step.
   */
  group(): fabric.Group | null {
    const canvas = this.editor.canvas;
    const active = canvas.getActiveObject();
    if (!active || active.type !== 'activeSelection') return null;
    let group: fabric.Group | null = null;
    const run = () => {
      group = (active as fabric.ActiveSelection).toGroup();
      canvas.requestRenderAll();
    };
    this.runBatched(run);
    this.editor.emit('selection:changed', { active: canvas.getActiveObjects() });
    return group;
  }

  /**
   * Ungroups the active `fabric.Group` back into an `ActiveSelection`. No-op
   * unless the active object is a group.
   */
  ungroup(): void {
    const canvas = this.editor.canvas;
    const active = canvas.getActiveObject();
    if (!active || active.type !== 'group') return;
    const run = () => {
      (active as fabric.Group).toActiveSelection();
      canvas.requestRenderAll();
    };
    this.runBatched(run);
    this.editor.emit('selection:changed', { active: canvas.getActiveObjects() });
  }

  // Runs `fn` as a single history step when the history plugin is installed,
  // otherwise runs it directly (`history?.batch(fn)` would silently skip fn).
  private runBatched(fn: () => void): void {
    if (this.editor.history) this.editor.history.batch(fn);
    else fn();
  }

  remove(obj: fabric.Object): void {
    this.editor.canvas.remove(obj);
    this.editor.canvas.requestRenderAll();
  }

  clear(): void {
    this.editor.canvas.clear();
    this.editor.canvas.requestRenderAll();
  }

  setProp<K extends keyof fabric.Object>(target: fabric.Object, key: K, value: fabric.Object[K]): void {
    target.set(key as keyof fabric.Object, value as never);
    target.setCoords();
    this.editor.canvas.requestRenderAll();
    this.editor.emit('object:modified', { target });
  }

  setData(target: fabric.Object, key: string, value: unknown): void {
    const prev = (target.data ?? {}) as Record<string, unknown>;
    const next = { ...prev };
    if (value === undefined) delete next[key];
    else next[key] = value;
    target.data = next;
    this.editor.canvas.requestRenderAll();
    this.editor.emit('object:modified', { target });
  }
}
